import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { TOAST_TYPE_ERROR } from "../services/constants";
import { showToastNotification } from "../reducers/toastSlice";
import { t, untranslated } from "@i18n";
import _ from "lodash";
import type { AnyAction, Dispatch } from "@reduxjs/toolkit";
import type {
  FileVersionData,
  GetFileVersionDataResponse,
  FileVersionsRequest,
  RemoveFileDependenciesRequest,
  StandardResponse,
  FileManagerPropsRequest,
  SaveCustomFieldsDataRequest,
  CreateFileVersionBody,
  FileVersionNoteRequest,
  FileResourcesUnderWhichFilesSharedRequest,
  SourceFileVersionData,
} from "@Types/fileOperations";
import type {
  GetFileVersionDataRequest,
  FileDependenciesRequest,
  SaveFileDependenciesRequest,
  CreateFileShortcutRequest,
  ConsumeExternalFileRequest,
  SaveCustomMetadataRequest,
  GetFileVersionsResponse,
  GetFileDependenciesResponse,
  SaveFileDependenciesResponse,
  RemoveFileDependencyResponse,
  GetResourcesUnderWhichFilesSharedResponse,
  CreateFileVersionSchemaResponse,
  SaveNoteInFileVersionResponse,
  CreateFileShortcutResponse,
  ConsumeExternalFilesResponse,
  GetFileManagerPropsResponse,
  SaveCustomFieldsDataResponse,
  SaveCustomMetadataSchemaResponse,
  SaveDependencyQuerySettingsRequest,
  SaveDependencyQuerySettingsResponse,
  GetDependencyQuerySettingsRequest,
  GetDependencyQuerySettingsResponse,
  FileDependencyItem,
  SharedResourceItem,
  SearchFilesByPropertyResponse,
} from "@ServerTypes/fileOperations/fileOperations.schemas.js";
import type {
  SaveMergeFileDataResponse,
  CreateNewContainerResponse,
  SaveEngineTypeResponse,
  ImportExcelContainersResponse,
} from "@ServerTypes/fileUpload/fileUpload.schema.js";
import type {
  FileSystemServerCrudResponse,
  CreateFolderRouteRequest,
  CreateFolderResponse,
} from "@ServerTypes/fileSystemServer/fileSystemServer.schema";
import type {
  CreateMergeEstimateRequest,
  CreateMergeEstimateResponse,
} from "@ServerTypes/estimates/estimates.schema.js";
import type { GetFormTemplateResponse } from "@ServerTypes/projects/project.schema.js";
import {
  setRefreshFileManager,
  setSelectedFiles,
  emptySelectedFiles,
  setCurrentFolder,
  emptyCurrentFolder,
  setFileManagerBreadCrumbData,
  type FolderItem,
  type SelectedFileItem,
  type BreadcrumbItem,
} from "../reducers/refreshFileManager";
import {
  setFileVersionData,
  mergeFileVersionData,
  setFilesDataAction,
  resetFilesData as resetFilesDataAction,
  setFileDependenciesAction,
  setDependencyQuerySettingsAction,
  setResourcesUnderWhichFilesSharedAction,
  setFileVersionIdAction,
} from "../reducers/fileVersion";
import {
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  setFileUploadProperties,
  minimiseFileUpload,
  maximiseFileUpload,
  closeFileUpload,
  setFileUploadData,
  removeFileUploadData,
  removeAllFileUploadData,
  updateFileUploadData,
  emptyFileUploadData,
  locationRemove,
  documentRemove,
  locationChange,
  documentChange,
} from "../reducers/minimiseFileUpload";

type AnyObject = Record<string, unknown>;
type AppDispatch = Dispatch<AnyAction>;
type ThunkAction = (dispatch: AppDispatch) => void;
type AsyncThunkAction<T = unknown> = (dispatch: AppDispatch) => Promise<T>;
type AxiosResponseLike<T> = { data: T };

type GetFileVersionsRequest = FileVersionsRequest;
type FileVersionDependenciesPayload = { fileVersionId: string; data: FileDependencyItem[] };
type FileResourcesSharedPayload = FileResourcesUnderWhichFilesSharedRequest;

interface FileNomenclatureRuleField {
  type?: string;
  allowedCharacters?: number;
  options?: string[];
}

interface FileNomenclatureRule {
  separator?: string;
  fields?: FileNomenclatureRuleField[];
}

interface UploadFileItem {
  name: string;
  size?: number;
  isFolder?: boolean;
  resourcesList?: UploadFileItem[];
  error?: boolean;
}

interface ExcelContainer {
  name: string;
  fileFormat?: string;
  folderPath?: string;
  [key: string]: unknown;
}

interface DirectImportContainer {
  name: string;
  fileFormat?: string;
  folderPath?: string;
  containerData?: AnyObject;
  [key: string]: unknown;
}

interface ProcessFileListParams {
  fileList: UploadFileItem[];
  currentFiles: Array<{ name: string; folderPath?: string }>;
  fileNomenclature?: FileNomenclatureRule;
  isFileVersionUploading?: boolean;
  uploadFolderData?: AnyObject;
  uploadSpaceId?: string;
  currentFolder?: AnyObject;
  isExcelImportFlow?: boolean;
  excelImportContainers?: ExcelContainer[];
  isDirectImportFlow?: boolean;
  directImportContainers?: DirectImportContainer[];
  matchByDocumentNumber?: boolean;
}

type DuplicateFileResult =
  | { found: false }
  | {
      found: true;
      newSuggestedName?: string;
      isDuplicateContainer?: boolean;
      noMatchingContainer?: boolean;
    };

type MatchFileToExcelContainerResult =
  | ExcelContainer
  | null
  | {
      error: true;
      message: string;
    };

export function setFileVersions(data: SourceFileVersionData[], fileId: string) {
  return setFileVersionData({ data, fileId });
}

export {
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  setFileUploadProperties,
  minimiseFileUpload,
  maximiseFileUpload,
  closeFileUpload,
};

export function setFilesData(data: Record<string, FileVersionData>) {
  return setFilesDataAction(data);
}

export function resetFilesData() {
  return resetFilesDataAction();
}

export {
  setFileUploadData,
  removeFileUploadData,
  removeAllFileUploadData,
  updateFileUploadData,
  emptyFileUploadData,
  locationRemove,
  documentRemove as documentTypeRemove,
  locationChange,
  documentChange as documentTypeChange,
};

export function setFileDependencies(data: FileVersionDependenciesPayload) {
  return setFileDependenciesAction(data);
}

export function setResourcesUnderWhichFilesShared(data: SharedResourceItem | null, fileId: string) {
  return setResourcesUnderWhichFilesSharedAction({ data, fileId });
}

export function mergeFileVersions(data: Record<string, unknown> & { fileId: string }) {
  return mergeFileVersionData(data);
}

export function refreshFileManager(data: boolean) {
  return setRefreshFileManager(data);
}
export function selectedFilesFileManager(data: SelectedFileItem[]) {
  return setSelectedFiles(data);
}

export function emptySelectedFilesFileManager() {
  return emptySelectedFiles();
}

export function setCurrentFolderInFileManager(data: FolderItem | null) {
  return setCurrentFolder(data);
}

export function emptyCurrentFolderInFileManager() {
  return emptyCurrentFolder();
}

export function fileManagerBreadCrumb(data: BreadcrumbItem[]) {
  return setFileManagerBreadCrumbData(data);
}

export function getFileVersions<T extends Exact<GetFileVersionsRequest, T>>(data: T): ThunkAction {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<GetFileVersionsResponse>(`${getBackendUrl()}/fileOperations/getFileVersions`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(setFileVersions((response.data.data ?? []) as SourceFileVersionData[], data.fileId));
        }
      })
      .catch((error) => {
        Promise.reject(error);
      });
  };
}

export function getFileDependencies<T extends Exact<FileDependenciesRequest, T>>(data: T & AnyObject): ThunkAction {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<GetFileDependenciesResponse>(`${getBackendUrl()}/fileOperations/getFileDependencies`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            setFileDependencies({
              fileVersionId: data.fileVersionId,
              data: (response.data.data ?? []) as FileDependencyItem[],
            }),
          );
        }
      })
      .catch((error) => {
        Promise.reject(error);
      });
  };
}

export async function saveFileDependencies(data: SaveFileDependenciesRequest & AnyObject) {
  const res = await axiosWrapper.post<SaveFileDependenciesResponse>(
    `${getBackendUrl()}/fileOperations/saveFileDependencies`,
    data,
  );
  return res;
}

export async function removeFileDependency(data: RemoveFileDependenciesRequest & AnyObject) {
  const res = await axiosWrapper.post<RemoveFileDependencyResponse>(
    `${getBackendUrl()}/fileOperations/removeFileDependency`,
    data,
  );
  return res;
}

export function getDependencyQuerySettings<T extends Exact<GetDependencyQuerySettingsRequest, T>>(
  data: T,
): ThunkAction {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<GetDependencyQuerySettingsResponse>(`${getBackendUrl()}/fileOperations/getDependencyQuerySettings`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            setDependencyQuerySettingsAction({
              fileVersionId: data.fileVersionId,
              data: response.data.data ?? null,
            }),
          );
        }
      })
      .catch((error) => {
        Promise.reject(error);
      });
  };
}

export function saveDependencyQuerySettings<T extends Exact<SaveDependencyQuerySettingsRequest, T>>(
  data: T,
): AsyncThunkAction<void> {
  return async (dispatch: AppDispatch) => {
    await axiosWrapper.post<SaveDependencyQuerySettingsResponse>(
      `${getBackendUrl()}/fileOperations/saveDependencyQuerySettings`,
      data,
    );
    dispatch(
      setDependencyQuerySettingsAction({
        fileVersionId: data.fileVersionId,
        data: data.dependencyQuerySettings,
      }),
    );
  };
}

export function getFileVersionData<T extends Exact<GetFileVersionDataRequest, T>>(
  data: T,
  signal?: AbortSignal,
): (dispatch: AppDispatch) => Promise<void> {
  return (dispatch: AppDispatch) => {
    return (
      axiosWrapper
        .post<GetFileVersionDataResponse>(`${getBackendUrl()}/fileOperations/getFileVersionData`, data, { signal })
        .then((response) => {
          // Caller navigated away (viewer unmounted / file switched) — drop the result.
          if (signal?.aborted) return;
          if (response.data.status && response.data.data) {
            dispatch(
              setFilesData({
                [data.fileVersionId]: response.data.data,
              }),
            );
          } else {
            dispatch(
              showToastNotification({
                content: response.data.message ? untranslated(response.data.message) : t("Something went wrong"),
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        })
        // eslint-disable-next-line catch-handler/require-promise-reject -- non-fatal per-file fetch failure (incl. expected abort on unmount); swallow so a single failed/cancelled file never rejects a runWithConcurrency batch
        .catch(() => {})
    );
  };
}

export function getResourcesUnderWhichFilesShared<T extends Exact<FileResourcesSharedPayload, T>>(
  data: T,
  returnValue = false,
): AsyncThunkAction<unknown> {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetResourcesUnderWhichFilesSharedResponse>(
      `${getBackendUrl()}/fileOperations/getResourcesUnderWhichFilesShared`,
      data,
    );
    if (response.data && !returnValue) {
      dispatch(setResourcesUnderWhichFilesShared(response.data as SharedResourceItem | null, data.fileId));
    }
    if (returnValue) {
      return response.data;
    }
  };
}

export function createFileVersion<T extends Exact<CreateFileVersionBody, T>>(data: T & AnyObject): ThunkAction {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<CreateFileVersionSchemaResponse>(`${getBackendUrl()}/fileOperations/createFileVersion`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(refreshFileManager(true));
        }
      })
      .catch((error) => {
        Promise.reject(error);
      });
  };
}

export function saveNoteInFileVersion<T extends Exact<FileVersionNoteRequest, T>>(data: T): () => void {
  return () => {
    axiosWrapper.post<SaveNoteInFileVersionResponse>(`${getBackendUrl()}/fileOperations/saveNoteInFileVersion`, data);
  };
}

export const createMergeFile = async (data: AnyObject): Promise<unknown> => {
  const response = await axiosWrapper.post<SaveMergeFileDataResponse>(
    `${getBackendUrl()}/fileUpload/createMergeFile`,
    data,
  );
  return response;
};
export const createFileShortcut = async <T extends Exact<CreateFileShortcutRequest, T>>(
  data: T,
): Promise<AxiosResponseLike<StandardResponse<unknown>>> => {
  const response = (await axiosWrapper.post<CreateFileShortcutResponse>(
    `${getBackendUrl()}/fileOperations/createFileShortcut`,
    data,
  )) as AxiosResponseLike<StandardResponse<unknown>>;
  return response;
};
export const consumeExternalFiles = async <T extends Exact<ConsumeExternalFileRequest, T>>(
  data: T,
): Promise<AxiosResponseLike<StandardResponse<unknown>>> => {
  const response = (await axiosWrapper.post<ConsumeExternalFilesResponse>(
    `${getBackendUrl()}/fileOperations/consumeExternalFiles`,
    data,
  )) as AxiosResponseLike<StandardResponse<unknown>>;
  return response;
};
export const getFilesData = async <T extends Exact<FileManagerPropsRequest, T>>(
  data: T,
): Promise<AxiosResponseLike<StandardResponse<unknown>>> => {
  const response = (await axiosWrapper.post<GetFileManagerPropsResponse>(
    `${getBackendUrl()}/fileOperations/getFileManagerProps`,
    data,
  )) as AxiosResponseLike<StandardResponse<unknown>>;
  return response;
};

export const createMergeEstimate = async <T extends Exact<CreateMergeEstimateRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<CreateMergeEstimateResponse>(
    `${getBackendUrl()}/estimate/createMergeEstimate`,
    data,
  );
  return response;
};

export const createContainer = async (data: AnyObject): Promise<unknown> =>
  await axiosWrapper.post<CreateNewContainerResponse>(`${getBackendUrl()}/fileUpload/createNewContainer`, data);

export const saveEngineType = async (data: AnyObject): Promise<AxiosResponseLike<StandardResponse>> =>
  (await axiosWrapper.post<SaveEngineTypeResponse>(
    `${getBackendUrl()}/fileUpload/saveEngineType`,
    data,
  )) as AxiosResponseLike<StandardResponse>;

export const createFolder = async (data: CreateFolderRouteRequest) =>
  await axiosWrapper.post<CreateFolderResponse>(`${getBackendUrl()}/fileSystemServer/createFolder`, data);

export const fetchFilesFromFolder = async (
  folderId: string,
  spaceId: string,
  spaceType = "this",
  includeRevisionHistory = false,
  // Both consumers (ExcelImport's `existingFiles` / `folderFilesMap`, and the FolderCache below)
  // already declare these rows as `Record<string, unknown>[]`.
): Promise<Record<string, unknown>[]> => {
  const response = await axiosWrapper.post<FileSystemServerCrudResponse>(
    `${getBackendUrl()}/fileSystemServer/readFiles`,
    {
      action: "read",
      path: "/",
      data: [{ hierarchyId: folderId, spaceId, spaceType }],
      includeRevisionHistory,
    },
  );

  const files = response.data?.files || [];

  if (!Array.isArray(files)) {
    return files ? [files] : [];
  }

  return files;
};

type FolderCache = Map<string, { hierarchyId: string; files: Record<string, unknown>[] }>;

export const ensureFolderPathExists = async (
  folderPath: string,
  baseHierarchyId: string,
  // Space `_id` — `objectIdString` in the createFolder request schema this forwards it to.
  spaceId: ObjectIdString,
  cache?: FolderCache,
): Promise<string> => {
  if (!folderPath || folderPath.trim() === "") {
    return baseHierarchyId;
  }

  const folderNames = folderPath.split("/").filter((name) => name.trim() !== "");
  let currentHierarchyId = baseHierarchyId;
  let currentPath = "";

  for (const folderName of folderNames) {
    currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;

    if (cache?.has(currentPath)) {
      currentHierarchyId = cache.get(currentPath)!.hierarchyId;
      continue;
    }

    const existingFiles = await fetchFilesFromFolder(currentHierarchyId, spaceId);
    const existingFolder = existingFiles.find(
      (file: Record<string, unknown>) => file.name === folderName && file.isFile === false,
    );

    if (existingFolder) {
      // Rows come back as opaque records; both id fields are stored as strings.
      currentHierarchyId = (existingFolder.hierarchyId || existingFolder._id) as string;
    } else {
      const response = await createFolder({
        action: "create",
        name: folderName,
        data: { hierarchyId: currentHierarchyId, name: folderName, spaceId },
      });

      if (response.data?.files?._id) {
        currentHierarchyId = response.data.files._id;
      }
    }

    if (cache) {
      cache.set(currentPath, { hierarchyId: currentHierarchyId, files: existingFiles });
    }
  }

  return currentHierarchyId;
};

export const ensureFolderPathExistsWithFiles = async (
  folderPath: string,
  baseHierarchyId: string,
  // Space `_id` — forwarded to ensureFolderPathExists, hence createFolder's `objectIdString`.
  spaceId: ObjectIdString,
  cache?: FolderCache,
): Promise<{ hierarchyId: string; files: Record<string, unknown>[] }> => {
  const cacheKey = folderPath || "__root__";

  if (cache?.has(cacheKey) && cache.get(cacheKey)!.files.length > 0) {
    return cache.get(cacheKey)!;
  }

  const hierarchyId = await ensureFolderPathExists(folderPath, baseHierarchyId, spaceId, cache);
  const cached = cache?.get(cacheKey);
  const files = cached?.files?.length ? cached.files : await fetchFilesFromFolder(hierarchyId, spaceId);

  const result = { hierarchyId, files };

  if (cache) {
    cache.set(cacheKey, result);
  }

  return result;
};

export function saveCustomFieldsData<T extends Exact<SaveCustomFieldsDataRequest, T>>(data: T) {
  return axiosWrapper.post<SaveCustomFieldsDataResponse>(
    `${getBackendUrl()}/fileOperations/saveCustomFieldsData`,
    data,
  );
}

export function saveCustomMetadata<T extends Exact<SaveCustomMetadataRequest, T>>(data: T) {
  return axiosWrapper.post<SaveCustomMetadataSchemaResponse>(
    `${getBackendUrl()}/fileOperations/saveCustomMetadata`,
    data,
  );
}

export function getFormTemplate(data: AnyObject) {
  return axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, data);
}

export function getFormTemplateId(data: AnyObject) {
  return axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplateId`, data);
}

export function setCurrentFileVersionId(data: string) {
  return setFileVersionIdAction(data);
}
export const importExcelContainers = async (data: AnyObject): Promise<unknown> =>
  await axiosWrapper.post<ImportExcelContainersResponse>(`${getBackendUrl()}/fileUpload/importExcelContainers`, data);

export { extractDocumentNumber } from "helpers/constants";

const checkForDuplicateFile = (
  fileToSearch: string,
  currentFiles: Array<{ name: string; folderPath?: string }>,
  fileFolderPath = "",
): DuplicateFileResult => {
  const currentFileExt = fileToSearch.split(".").pop();
  const currentFileName = fileToSearch.split(".").slice(0, -1).join(".").trim();

  let filteredFiles = currentFiles.filter((file) => file.name.split(".").pop() === currentFileExt);

  if (fileFolderPath) {
    filteredFiles = filteredFiles.filter((file) => (file.folderPath || "") === fileFolderPath);
  } else {
    filteredFiles = filteredFiles.filter((file) => !file.folderPath || file.folderPath === "");
  }

  let duplicateCount = 0;
  filteredFiles.forEach((file) => {
    const fileName = file.name.split(".").slice(0, -1).join(".").trim();
    const versionedName = fileName.replace(/\([0-9]+\)$/, "").trim();

    if (fileName === currentFileName || versionedName === currentFileName) {
      duplicateCount++;
    }
  });

  return duplicateCount === 0
    ? { found: false }
    : { found: true, newSuggestedName: `${currentFileName}(${duplicateCount}).${currentFileExt}` };
};

const matchNomenclature = (file: UploadFileItem, fileNomenclatureRule: FileNomenclatureRule) => {
  let fileName = file.name.split(".");
  fileName.pop();
  fileName = fileName.join(".").split(fileNomenclatureRule.separator ?? "");
  const isFileNomenclatureCorrect = fileNomenclatureRule.fields?.every((rule, index) => {
    let output = false;

    if ((rule.type == "text" || rule.type == "number") && !rule.allowedCharacters) {
      return true;
    }

    const textCondition = rule.type == "text" && fileName[index]?.length == rule.allowedCharacters;
    const numberCondition = rule.type == "number" && fileName[index]?.length == rule.allowedCharacters;
    const selectCondition = rule.type == "single select" && rule.options?.includes(fileName[index] ?? "");

    if (textCondition || numberCondition || selectCondition) {
      output = true;
    }
    return output;
  });
  return isFileNomenclatureCorrect ?? true;
};

const matchFileToExcelContainer = (
  fileName: string,
  excelContainers: ExcelContainer[],
  fileFolderPath = "",
): MatchFileToExcelContainerResult => {
  const lastDotIndex = fileName.lastIndexOf(".");
  const fileNameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const fileExtension = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toLowerCase() : "";

  const allNameMatches: Array<{ container: ExcelContainer; containerFolderPath: string }> = [];
  let exactMatch: ExcelContainer | null = null;

  for (const container of excelContainers) {
    const nameMatches = container.name === fileNameWithoutExt;
    const formatMatches = container.fileFormat?.toLowerCase() === fileExtension;

    if (nameMatches && formatMatches) {
      const containerFolderPath = container.folderPath || "";
      allNameMatches.push({ container, containerFolderPath });

      if (fileFolderPath === containerFolderPath) {
        exactMatch = container;
      }
    }
  }

  if (allNameMatches.length > 1 && !exactMatch) {
    return {
      error: true,
      message: `Multiple containers named "${fileNameWithoutExt}" exist in different folders. Please upload with folder structure to match correctly.`,
    };
  }

  return exactMatch || (allNameMatches.length === 1 ? (allNameMatches[0]?.container ?? null) : null);
};

type MatchFileToDirectImportContainerResult =
  | DirectImportContainer
  | null
  | {
      error: true;
      message: string;
    };

const matchFileToDirectImportContainer = (
  fileName: string,
  directImportContainers: DirectImportContainer[],
  fileFolderPath = "",
): MatchFileToDirectImportContainerResult => {
  const lastDotIndex = fileName.lastIndexOf(".");
  const fileNameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const fileExtension = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toLowerCase() : "";

  const allNameMatches: Array<{ container: DirectImportContainer; containerFolderPath: string }> = [];
  let exactMatch: DirectImportContainer | null = null;

  for (const container of directImportContainers) {
    const nameMatches = container.name === fileNameWithoutExt;
    const formatMatches = container.fileFormat?.toLowerCase() === fileExtension;

    if (nameMatches && formatMatches) {
      const containerFolderPath = container.folderPath || "";
      allNameMatches.push({ container, containerFolderPath });

      if (fileFolderPath === containerFolderPath) {
        exactMatch = container;
      }
    }
  }

  if (allNameMatches.length > 1 && !exactMatch) {
    return {
      error: true,
      message: `Multiple containers named "${fileNameWithoutExt}" exist in different folders. Please upload with folder structure to match correctly.`,
    };
  }

  return exactMatch || (allNameMatches.length === 1 ? (allNameMatches[0]?.container ?? null) : null);
};

export const processFileList = ({
  fileList,
  currentFiles,
  fileNomenclature,
  isFileVersionUploading,
  uploadFolderData,
  uploadSpaceId,
  currentFolder,
  isExcelImportFlow = false,
  excelImportContainers = [],
  isDirectImportFlow = false,
  directImportContainers = [],
  matchByDocumentNumber = false,
}: ProcessFileListParams) =>
  fileList.map((file: UploadFileItem) => {
    const { name, size, isFolder = false, resourcesList, error } = file;
    let duplicateFileStatus: DuplicateFileResult = { found: false };
    let isFileNomenclatureCorrect = true;

    if (fileNomenclature && Object.keys(fileNomenclature).length) {
      if (!isFolder) {
        isFileNomenclatureCorrect = matchNomenclature(file, fileNomenclature);
      } else if (resourcesList?.length) {
        isFileNomenclatureCorrect = resourcesList.every((resource: UploadFileItem) =>
          matchNomenclature(resource, fileNomenclature),
        );
      }
    }

    let matchedContainer: ExcelContainer | null = null;
    let matchedDirectImportContainer: DirectImportContainer | null = null;
    let excelImportError: string | null = null;
    let directImportError: string | null = null;

    if (isDirectImportFlow && directImportContainers.length > 0) {
      const matchResult = matchFileToDirectImportContainer(name, directImportContainers, "");

      if (matchResult && typeof matchResult === "object" && "error" in matchResult && matchResult.error === true) {
        duplicateFileStatus = { found: true, isDuplicateContainer: true };
        directImportError = matchResult.message as string;
        matchedDirectImportContainer = null;
      } else if (!matchResult) {
        directImportError = "No matching container found in Excel. File will be uploaded to current folder.";
        matchedDirectImportContainer = null;
      } else {
        matchedDirectImportContainer = matchResult as DirectImportContainer;
      }
      if (!duplicateFileStatus.found && !matchByDocumentNumber) {
        const targetFolderPath = matchedDirectImportContainer
          ? matchedDirectImportContainer.folderPath || ""
          : "__CURRENT_FOLDER__";
        duplicateFileStatus = checkForDuplicateFile(name, currentFiles, targetFolderPath);
      }
    } else if (isExcelImportFlow && excelImportContainers.length > 0) {
      const matchResult = matchFileToExcelContainer(name, excelImportContainers, "");

      if (matchResult && typeof matchResult === "object" && "error" in matchResult && matchResult.error === true) {
        duplicateFileStatus = { found: true, isDuplicateContainer: true };
        excelImportError = matchResult.message as string;
        matchedContainer = null;
      } else if (!matchResult) {
        duplicateFileStatus = { found: true, noMatchingContainer: true };
        excelImportError = "No matching container found in Excel import. File will not be uploaded.";
      } else {
        matchedContainer = matchResult as ExcelContainer;
      }
    } else if (isFileVersionUploading !== true) {
      duplicateFileStatus = checkForDuplicateFile(name, currentFiles, "");
    }

    const isDuplicate = duplicateFileStatus.found;
    return {
      name,
      size,
      duplicateFileStatus,
      isDuplicate,
      progress: 0,
      bytesUploaded: 0,
      progressCompleted: false,
      error:
        excelImportError ||
        (isDirectImportFlow &&
          duplicateFileStatus.found &&
          (duplicateFileStatus as Extract<DuplicateFileResult, { found: true }>).isDuplicateContainer) ||
        error ||
        false,
      status: excelImportError
        ? "Rejected"
        : isDirectImportFlow &&
            duplicateFileStatus.found &&
            (duplicateFileStatus as Extract<DuplicateFileResult, { found: true }>).isDuplicateContainer
          ? "Rejected"
          : "Processing...",
      isMetadataPending: true,
      triggerFileUpload: false,
      file,
      isFileNomenclatureCorrect,
      isFolder,
      hierarchyId: _.get(uploadFolderData, "id", _.get(currentFolder, "hierarchyId")),
      spaceId: uploadSpaceId,
      isExcelImportFlow,
      matchedContainer,
      excelImportError,
      isDirectImportFlow,
      matchedDirectImportContainer,
      directImportError,
      createContainerOnUpload: isDirectImportFlow && !matchedDirectImportContainer,
      uploadLocation: isDirectImportFlow
        ? matchedDirectImportContainer?.folderPath
          ? `/${matchedDirectImportContainer.folderPath}`
          : `/${_.get(uploadFolderData, "name") || ""}`
        : null,
    };
  });

export const handlePropertiesSeach = async (data: Record<string, unknown>, signal: AbortSignal) => {
  const res = await axiosWrapper.post<SearchFilesByPropertyResponse>(
    `${getBackendUrl()}/fileOperations/searchFilesByProperty`,
    data,
    { signal },
  );
  return res.data.result;
};
